/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import SociollaText from '@components/sociolla/SociollaText';
import { Loader } from '@components/screen/Layout';
import Image from '@components/global/Image';
import Toast from 'react-native-toast-message';
import ImageContainV2 from '@components/global/ImageContainV2';
import TitleSubSection from '@components/global/TitleSubSection';
import ProductItem from '@components/global/ProductItem';
import StatusBadge from '@components/global/StatusBadge';
import Table from '@components/table/Table';
import { downloadFile } from '@global/helpers';

import Colors from '@global/colors';
import { isWeb, isNative, fullWidth } from '@global/screens';
import { MsCatalog } from '@/api/ms-catalog';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';

const Sections = ({
  isEdit,
  detail = {},
  pageType = 'info',
  sections = [],
  loading = false,
  onReload = () => {},
  onEdit = () => {},
  isCMSDetail = false,
  onSectionsUpdate = () => {},
}) => {
  const dispatch = useDispatch();
  const [imageWidth, setImageWidth] = useState(0);

  const tableStructure = {
    image: {
      head: 'Image',
      body: 'products.combination.image',
      type: 'image',
    },
    product_name: {
      head: 'Product Name',
      body: 'products.name',
    },
    price: {
      head: 'Sales Price',
      body: 'products.price',
      type: 'currency',
    },
    ...(!isCMSDetail
      ? {
          action: {
            head: '',
            type: 'action_icon',
            actions: [
              {
                name: 'delete',
              },
            ],
          },
        }
      : {}),
  };

  const tableStructurePromo = {
    name: {
      head: 'Rules Name',
      body: 'promo_rule.name',
      textStyle: { fontFamily: 'figtree-medium' },
    },
    promotion_type: {
      head: 'Promo Type',
      body: 'promo_rule.promotion_type',
    },
    periode: {
      head: 'Periode',
      body: 'promo_rule.periode',
    },
    ...(isCMSDetail
      ? {
          is_active: {
            head: 'Status',
            body: 'promo_rule.is_active',
            type: 'status_active',
          },
        }
      : {
          action: {
            head: '',
            type: 'action_icon',
            actions: [
              {
                name: 'delete',
              },
            ],
          },
        }),
  };

  const onRemoveSection = async (id, type, localId = '') => {
    if (!id) {
      const updatedSections = sections.filter(
        (section) => section.localId !== localId,
      );
      onSectionsUpdate(updatedSections);
      return;
    }
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const response = await MsCatalog.deleteCMSSection(detail?._id, type, id);
      if (response?.data?.data) {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        onReload();
      } else {
        dispatch(showPopup({ name: 'isLoading', value: false }));
      }
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const onRemovePdfItem = async (item, pdfIdx) => {
    const newPdfDocuments = item.pdf_documents.filter(
      (_, idx) => idx !== pdfIdx,
    );

    if (!item._id) {
      if (newPdfDocuments.length === 0) {
        onSectionsUpdate(
          sections.filter((section) => section.localId !== item.localId),
        );
      } else {
        onSectionsUpdate(
          sections.map((section) =>
            section.localId === item.localId
              ? { ...section, pdf_documents: newPdfDocuments }
              : section,
          ),
        );
      }
      return;
    }

    if (newPdfDocuments.length === 0) {
      onRemoveSection(item._id, 'pdf_documents');
      return;
    }

    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      await MsCatalog.updateCMSSection(detail?._id, 'pdf_documents', item._id, {
        pdf_documents: newPdfDocuments,
      });
      dispatch(showPopup({ name: 'isLoading', value: false }));
      onReload();
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const onRemoveProduct = (id) => {
    dispatch(
      showAlert({
        show: true,
        title: 'Remove Product',
        description: 'This product will be deleted. Do you want to proceed?',
        onCallback: () => {
          const updatedSections = [...sections]; // Create a copy of the sections array

          for (let i = 0; i < updatedSections.length; i++) {
            const section = updatedSections[i];
            if (section.type === 'products') {
              const indexToRemove = section.products.findIndex(
                (product) => product.products.id === id,
              );
              if (indexToRemove !== -1) {
                section.products.splice(indexToRemove, 1);
                if (section.products.length === 0) {
                  updatedSections.splice(i, 1);
                }
                break;
              }
            }
          }

          onSectionsUpdate(updatedSections);
        },
      }),
    );
  };

  const onMapActionProduct = ({ data, index, action }) => {
    if (data) {
      if (action.name == 'delete') {
        if (isEdit) {
          onRemoveSection(data._id ? data._id : data.id, 'products');
        } else {
          onRemoveProduct(data.products.id);
        }
      }
    }
  };

  const onMapAction = ({ data, index, action }) => {
    if (data) {
      if (action.name == 'delete') {
        onRemoveSection(data._id ? data._id : data.id, 'promotions');
      }
    }
  };

  const onLayout = (e) => {
    if (e?.nativeEvent?.layout?.width) {
      setImageWidth(e.nativeEvent.layout.width);
    }
  };

  const imageStyle = (item) => {
    const column = item.image.length;
    let setwidth = '';
    if (column === 1) {
      setwidth = 500;
    } else {
      if (!isNative && !imageWidth) {
        setwidth = `calc((100% / ${column}) - 10px)`;
      } else {
        setwidth = (imageWidth ? imageWidth : fullWidth - 30) / column - 10;
      }
    }
    return {
      width: setwidth,
      marginRight: 10,
      marginBottom: 10,
    };
  };

  const openPDF = async (url) => {
    if (!url) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      return;
    }

    try {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      await downloadFile(url, 'pdf-section', 'pdf', true);
    } catch (err) {
      Toast.show({
        text1: 'A system error occurred',
        type: 'error',
      });
    } finally {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const generatePdfName = (index) => {
    return `pdf-section-${index + 1}.pdf`;
  };

  return (
    <View
      style={{ width: '100%', paddingTop: isWeb ? 10 : 0 }}
      onLayout={onLayout}>
      {loading ? (
        <Loader />
      ) : !sections?.length ? (
        <View
          style={{
            width: '100%',
            paddingVertical: 30,
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <SociollaText style={{ marginVertical: 10 }}>
            {pageType == 'info'
              ? 'No section found'
              : pageType == 'guideline'
              ? 'No PDF section added'
              : 'No promotion rules added'}
          </SociollaText>
        </View>
      ) : (
        <>
          {sections.map((item, idx) => (
            <View key={`parent-${idx}`}>
              {item.type == 'text' ? ( // Text Section
                <View key={idx} style={styles.mBottom}>
                  <TitleSubSection
                    title="TEXT SECTION"
                    disabled={loading}
                    customButton={
                      !isCMSDetail ? (
                        <View style={styles.row}>
                          <TouchableOpacity
                            activeOpacity={0.8}
                            onPress={() =>
                              onEdit({
                                type: 'text',
                                data: item.description,
                                id: item._id,
                                localId: item?.localId || '',
                              })
                            }>
                            <SociollaText
                              type="medium"
                              style={styles.buttonText}>
                              Edit
                            </SociollaText>
                          </TouchableOpacity>
                          <TouchableOpacity
                            activeOpacity={0.8}
                            style={styles.mLeft}
                            onPress={() =>
                              onRemoveSection(item._id, 'text', item?.localId)
                            }>
                            <SociollaText
                              type="medium"
                              style={styles.buttonTextRemove}>
                              Hapus
                            </SociollaText>
                          </TouchableOpacity>
                        </View>
                      ) : (
                        false
                      )
                    }
                  />
                  <View style={styles.vPadding}>
                    <SociollaText>{item.description}</SociollaText>
                  </View>
                </View>
              ) : item.type == 'images' ? ( // Image Section
                <View key={idx} style={styles.mBottom}>
                  <TitleSubSection
                    title="IMAGE SECTION"
                    disabled={loading}
                    customButton={
                      !isCMSDetail ? (
                        <View style={styles.row}>
                          <TouchableOpacity
                            activeOpacity={0.8}
                            onPress={() =>
                              onEdit({
                                type: 'image',
                                data: item.image,
                                id: item._id,
                                localId: item?.localId || '',
                              })
                            }>
                            <SociollaText
                              type="medium"
                              style={styles.buttonText}>
                              Edit
                            </SociollaText>
                          </TouchableOpacity>
                          <TouchableOpacity
                            activeOpacity={0.8}
                            style={styles.mLeft}
                            onPress={() =>
                              onRemoveSection(item._id, 'images', item?.localId)
                            }>
                            <SociollaText
                              type="medium"
                              style={styles.buttonTextRemove}>
                              Hapus
                            </SociollaText>
                          </TouchableOpacity>
                        </View>
                      ) : (
                        false
                      )
                    }
                  />
                  <View
                    style={[styles.row, styles.vPadding, { flexWrap: 'wrap' }]}>
                    {item.image.map((img, imgIdx) => (
                      <ImageContainV2
                        source={img.url}
                        style={imageStyle(item)}
                        key={imgIdx}
                      />
                    ))}
                  </View>
                </View>
              ) : item.type == 'products' ? ( // Product Section
                <View key={idx} style={styles.mBottom}>
                  <TitleSubSection title="PRODUCT SECTION" />
                  <View style={styles.vPadding}>
                    {isWeb ? (
                      <Table
                        structures={tableStructure}
                        data={item.products}
                        withFetch={false}
                        hasNoHide
                        showFilter={false}
                        noTopSection
                        showPagination={false}
                        withoutShadow
                        totalData={item.products.length}
                        onMapAction={(val) => onMapActionProduct(val)}
                      />
                    ) : (
                      item.products.map((item, idx) => {
                        const pr = { _id: item._id, ...item.product };
                        return (
                          <ProductItem
                            data={pr}
                            isDetail={isCMSDetail}
                            onRemove={() =>
                              onMapActionProduct({
                                data: pr,
                                index: idx,
                                action: { name: 'delete' },
                              })
                            }
                            withoutQuantity
                            textWidth={fullWidth - 100}
                            showRemove={!isCMSDetail}
                            key={idx}
                          />
                        );
                      })
                    )}
                  </View>
                </View>
              ) : item.type == 'promotions' ? (
                <View style={styles.vPadding} key={idx}>
                  {isWeb ? (
                    <Table
                      structures={tableStructurePromo}
                      data={item.promotions}
                      withFetch={false}
                      hasNoHide
                      showFilter={false}
                      noTopSection
                      showPagination={false}
                      withoutShadow
                      totalData={item.promotions.length}
                      onMapAction={(val) => onMapAction(val)}
                    />
                  ) : (
                    item.promotions.map((item, idx) => (
                      <View style={styles.promotionItem} key={idx}>
                        <View
                          style={[
                            styles.promotionItemSection,
                            { marginBottom: 10 },
                          ]}>
                          <SociollaText type="medium" style={{ fontSize: 15 }}>
                            {item?.promo_rule?.name
                              ? item.promo_rule.name
                              : '-'}
                          </SociollaText>
                          <StatusBadge
                            status={
                              item?.promo_rule?.is_active
                                ? 'active'
                                : 'in_active'
                            }
                          />
                        </View>
                        <View style={styles.promotionItemSection}>
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                            }}>
                            <Image
                              style={{
                                width: 19,
                                height: 14,
                                marginLeft: -5,
                                marginRight: 5,
                              }}
                              width={19}
                              height={14}
                              svg={true}
                              source={require('@assets/img/icons/menu/calendar.svg')}
                            />
                            <SociollaText
                              style={{ fontSize: 11, color: Colors.grey2 }}>
                              {item?.promo_rule?.periode
                                ? item?.promo_rule?.periode
                                : '-'}
                            </SociollaText>
                          </View>
                          <SociollaText
                            style={{ fontSize: 11, color: Colors.grey1 }}>
                            Type: {item?.promo_rule?.promotion_type}
                          </SociollaText>
                        </View>
                      </View>
                    ))
                  )}
                </View>
              ) : item.type == 'pdf_documents' ? (
                <View key={idx} style={styles.mBottom}>
                  <TitleSubSection
                    title="PDF SECTION"
                    disabled={loading}
                    customButton={
                      !isCMSDetail ? (
                        <View style={styles.row}>
                          <TouchableOpacity
                            activeOpacity={0.8}
                            style={styles.mLeft}
                            onPress={() =>
                              onRemoveSection(
                                item._id,
                                'pdf_documents',
                                item?.localId,
                              )
                            }>
                            <SociollaText
                              type="medium"
                              style={styles.buttonTextRemove}>
                              Hapus
                            </SociollaText>
                          </TouchableOpacity>
                        </View>
                      ) : (
                        false
                      )
                    }
                  />
                  <View
                    style={[
                      styles.pdfList,
                      styles.vPadding,
                      { flexWrap: 'wrap' },
                    ]}>
                    {item.pdf_documents.map((pdf, pdfIdx) => (
                      <TouchableOpacity
                        activeOpacity={0.6}
                        onPress={() => openPDF(pdf.url)}
                        style={styles.pdfbox}
                        key={pdfIdx}>
                        {!isCMSDetail ? (
                          <TouchableOpacity
                            activeOpacity={0.8}
                            style={styles.pdfDeleteBtn}
                            onPress={(e) => {
                              e?.stopPropagation?.();
                              onRemovePdfItem(item, pdfIdx);
                            }}>
                            <Image
                              style={{ width: 10, height: 10 }}
                              width={10}
                              height={10}
                              svg={true}
                              source={require('@assets/img/icons/close-white.svg')}
                            />
                          </TouchableOpacity>
                        ) : (
                          false
                        )}
                        <Image
                          style={{ width: 35, height: 43 }}
                          width={35}
                          height={43}
                          source={require('@assets/img/icons/pdf-download.svg')}
                        />
                        <SociollaText
                          style={styles.pdfTitle}
                          numberOfLines={1}
                          ellipsizeMode="tail">
                          {pdf.description || generatePdfName(pdfIdx)}
                        </SociollaText>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              ) : (
                false
              )}
            </View>
          ))}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row' },
  mLeft: { marginLeft: 15 },
  mBottom: { marginBottom: 15 },
  buttonText: { fontSize: 13, color: Colors.blue0 },
  buttonTextRemove: { fontSize: 13, color: Colors.red0 },
  vPadding: isWeb ? { paddingVertical: 15 } : { padding: 15 },
  promotionItem: {
    width: '100%',
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  promotionItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  pdfList: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  pdfbox: {
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.blue0,
    padding: 8,
    marginRight: 10,
    marginBottom: 10,
    position: 'relative',
    width: 100,
  },
  pdfDeleteBtn: {
    position: 'absolute',
    top: -8,
    right: -8,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: Colors.red0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  pdfTitle: { marginTop: 10, width: '100%', textAlign: 'center' },
});

export default Sections;
